// Shared drawing pieces for the /face-data-changes-2026 animations: a drawn photo of a runner (so the face
// outline always sits on the face), a grid of face-template dots, and the deterministic directions they scatter in
// when the template dissolves.
import type { CSSProperties } from "react";

/** Where each of `n` template dots drifts as it dissolves (px): outwards and up, deterministic for SSR. */
export const scatter = (n: number, dist = 26): [number, number][] =>
  Array.from({ length: n }, (_, i) => {
    const a = -Math.PI / 2 + ((i * 137.5) % 360) * (Math.PI / 180) * 0.9 - 0.8;
    const d = dist * (0.6 + ((i * 7) % 5) / 10);
    return [Math.round(Math.cos(a) * d), Math.round(Math.sin(a) * d - 8)];
  });

/** A rows × cols grid of dots joined by thin lines: a face template. Each dot can carry its own animation. */
export const TemplateDots = ({
  cols, rows, x, y, gap, r, dotStyle, lineStyle,
}: {
  cols: number; rows: number; x: number; y: number; gap: number; r: number;
  dotStyle: (i: number) => CSSProperties;
  lineStyle?: CSSProperties;
}) => {
  const pts = Array.from({ length: cols * rows }, (_, i) => ({ cx: x + (i % cols) * gap, cy: y + Math.floor(i / cols) * gap }));
  return (
    <g>
      {lineStyle && (
        <g data-a="" style={lineStyle} className="stroke-primary" strokeWidth={0.8} opacity={0.55}>
          {pts.map((p, i) => (
            <g key={i}>
              {i % cols < cols - 1 && <line x1={p.cx} y1={p.cy} x2={pts[i + 1].cx} y2={pts[i + 1].cy} />}
              {i + cols < pts.length && <line x1={p.cx} y1={p.cy} x2={pts[i + cols].cx} y2={pts[i + cols].cy} />}
              {i % cols < cols - 1 && i + cols < pts.length && (i + Math.floor(i / cols)) % 2 === 0 && (
                <line x1={p.cx} y1={p.cy} x2={pts[i + cols + 1].cx} y2={pts[i + cols + 1].cy} />
              )}
            </g>
          ))}
        </g>
      )}
      {pts.map((p, i) => (
        <circle key={i} data-a="" cx={p.cx} cy={p.cy} r={r} className="fill-primary" style={dotStyle(i)} />
      ))}
    </g>
  );
};

/**
 * A drawn event photo, w × h at the origin: sky, ground, a runner in an amber shirt with bib 1234. The face
 * outline (the face data) carries `faceStyle`.
 */
export const Runner = ({ w, h, clipId, faceStyle, bib = "1234" }: { w: number; h: number; clipId: string; faceStyle: CSSProperties; bib?: string }) => {
  const k = h / 72; // drawn at 84 × 72, scaled
  const cx = w / 2;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect width={w} height={h} rx={8 * k} />
        </clipPath>
        <linearGradient id={`${clipId}-sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#bfe3f2" />
          <stop offset="1" stopColor="#e8f4ea" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect width={w} height={h} fill={`url(#${clipId}-sky)`} />
        <rect y={h * 0.72} width={w} height={h * 0.28} fill="#8fc27a" />
        <rect y={h * 0.8} width={w} height={h * 0.2} fill="#c9b79a" />
        {/* runner */}
        <g transform={`translate(${cx},${0}) scale(${k})`}>
          <path d="M -6 46 L -12 64 M 6 46 L 13 62" stroke="#2b3445" strokeWidth={4.5} strokeLinecap="round" />
          <path d="M -9 28 L -17 40 M 9 28 L 16 38" stroke="#e6b48f" strokeWidth={4} strokeLinecap="round" />
          <rect x={-10} y={25} width={20} height={23} rx={5} fill="#EA8A23" />
          <rect x={-7} y={31} width={14} height={10} rx={1.5} fill="#ffffff" />
          <text x={0} y={38.6} fontSize={6} fontWeight={700} textAnchor="middle" fill="#1a1a1a">{bib}</text>
          <circle cx={0} cy={16} r={8} fill="#e6b48f" />
          <path d="M -8 14 Q 0 3 8 14 Q 4 9 0 9.5 Q -5 9.5 -8 14 Z" fill="#4a3426" />
        </g>
      </g>
      <rect width={w} height={h} rx={8 * k} fill="none" className="stroke-border" strokeWidth={1} />
      {/* the face outline: this is what the face data describes */}
      <g data-a="" style={faceStyle}>
        <rect x={cx - 12 * k} y={4 * k} width={24 * k} height={24 * k} rx={4} fill="none" className="stroke-primary" strokeWidth={1.8} strokeDasharray="4 2.5" />
      </g>
    </g>
  );
};
